import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export const LIGHT_THEME = 'teegle-day';
export const DARK_THEME = 'teegle-night';
const THEME_STORAGE_KEY = 'teegle-theme';

export type AppTheme = typeof LIGHT_THEME | typeof DARK_THEME;

interface ThemeContextValue {
    theme: AppTheme;
    isDark: boolean;
    setTheme: (theme: AppTheme) => void;
    toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function isAppTheme(value: string | null): value is AppTheme {
    return value === LIGHT_THEME || value === DARK_THEME;
}

export function getInitialTheme(): AppTheme {
    if (typeof window === 'undefined') {
        return LIGHT_THEME;
    }

    const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isAppTheme(storedTheme) ? storedTheme : LIGHT_THEME;
}

export function applyTheme(theme: AppTheme) {
    if (typeof document === 'undefined') {
        return;
    }

    document.documentElement.dataset.theme = theme;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
    const [theme, setTheme] = useState<AppTheme>(() => getInitialTheme());

    useEffect(() => {
        applyTheme(theme);
        window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    }, [theme]);

    const value = useMemo<ThemeContextValue>(
        () => ({
            theme,
            isDark: theme === DARK_THEME,
            setTheme,
            toggleTheme: () =>
                setTheme((current) => (current === LIGHT_THEME ? DARK_THEME : LIGHT_THEME)),
        }),
        [theme]
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within ThemeProvider');
    }
    return context;
}
